import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { SendingOrderStatusBadge } from 'src/modules/sending/components/sendingOrderStatusBadge';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';
import { getAllSendingOrder, deleteSendingOrder } from 'src/shared/services/sendingService';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import dayjs from 'dayjs';

const SendingGrid = () => {
  const [rows, setRows] = useState<SendingOrder[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);

    const result = await getAllSendingOrder(data.page, data.pageSize, 'id', '');
    updateData('totalRows', result.data.totalItems);

    setTimeout(() => {
      setRows(result.data.results);
      updateData('loading', false);
    }, 100);
  }, [data.page, data.pageSize]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const [value, setValue] = React.useState('');

  const showDeleteConfirmationDialog = (id?: any) => {
    setValue(id);
  };

  const sendingOrderTypes = ['', 'Brev', 'Faktura', 'Medlemskort'];

  const createEditRoute = (id?: any) => {
    return RoutesEnum.SendingOrderDetail + (`/${id}` || '');
  };

  const showProduct = (id?: any) => {
    return sendingOrderTypes[id];
  };

  const showDate = (dateCreated?: any) => {
    return dayjs(new Date(dateCreated)).format('YYYY-MM-DD HH:mm');
  };

  const deleteRow = async (id?: any) => {
    await deleteSendingOrder(id);
    toast.success('Order borttagen!', {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    fetchData();
  };

  const handleClose = (newValue?: any) => {
    ConsoleHelper.log('handleClose', newValue);
    setValue('');

    if (newValue) {
      deleteRow(newValue.id);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'product_id',
      headerName: 'Typ av utskick',
      width: 170,
      renderCell: (params: GridCellParams) => <>{showProduct(params.row.product_id)}</>,
    },
    {
      field: 'date_id',
      headerName: 'Utskicksdatum',
      sortable: true,
      width: 150,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.date_id}</>;
      },
    },
    {
      field: 'createdByName',
      headerName: 'Kund',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <>
          <a href={createEditRoute(params.row.id)}>{params.row.createdByName || ''}</a>
          &nbsp;
          <small>{params.row.createdByEmail}</small>
        </>
      ),
    },
    {
      field: 'updated',
      headerName: 'Status',
      sortable: true,
      width: 150,
      renderCell: () => {
        return <SendingOrderStatusBadge />;
      },
    },
    {
      field: 'created',
      headerName: 'Skickat',
      flex: 1,
      renderCell: (params: GridCellParams) => <>{showDate(params.row.created)}</>,
    },
    {
      field: 'id',
      headerName: 'Radera',
      flex: 0.3,
      minWidth: 20,
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <IconButton aria-label='delete' onClick={() => showDeleteConfirmationDialog(params.row.id)} size='large'>
          <DeleteIcon fontSize='small' />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <DataGrid<SendingOrder>
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
      <DeleteConfirmationDialog
        title='Radera?'
        modaltext='To subscribe to this website, please enter your email address here. We will send updates occasionally.'
        open={!!value}
        onClose={handleClose}
        value={value}
      />
    </>
  );
};

export default SendingGrid;
